import { CloseOutlined } from '@mui/icons-material';
import { Button, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import React, { useState } from 'react';

import { Input } from '@/components/common/Input';
import { useOrganizations } from '@/hooks/queries/useOrganizations';
import { useRoles } from '@/hooks/queries/useRoles';
import type { CreateEmployeeData } from '@/types/employees';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEmployeeData) => void;
  isLoading?: boolean;
  isSuperAdmin?: boolean;
  organizationId?: string;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  isSuperAdmin = false,
  organizationId,
}) => {
  const [formData, setFormData] = useState<CreateEmployeeData>({
    name: '',
    email: '',
    password: '',
    roleId: '',
  });

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    organizationId || ''
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch organizations (only for super admins)
  const { data: organizationsData } = useOrganizations(1, 100, 'ASC');

  // Fetch roles for the selected organization (super admin) or user's organization (admin)
  const effectiveOrgId = isSuperAdmin ? selectedOrganizationId : organizationId;
  const { data: rolesData } = useRoles(effectiveOrgId);

  // Build organization dropdown options (only for super admins)
  const organizationOptions: DropdownOption[] = React.useMemo(() => {
    if (!isSuperAdmin || !organizationsData?.data) return [];
    return organizationsData.data.map(org => ({
      value: org.id,
      label: org.name,
    }));
  }, [organizationsData, isSuperAdmin]);

  // Build dropdown options - filter based on user role
  const roleOptions: DropdownOption[] = React.useMemo(() => {
    if (!rolesData?.data) return [];

    // Filter roles based on current user's permissions
    const filteredRoles = rolesData.data.filter(role => {
      if (isSuperAdmin) {
        // Super admins can create super_admin, admin, and manager
        return ['super_admin', 'admin', 'manager'].includes(role.name);
      } else {
        // Admins can only create admin and manager roles within their organization
        return ['admin', 'manager'].includes(role.name);
      }
    });

    return filteredRoles.map(role => ({
      value: role.id,
      label:
        role.name === 'super_admin'
          ? 'Super Admin'
          : role.name.charAt(0).toUpperCase() + role.name.slice(1),
    }));
  }, [rolesData, isSuperAdmin]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors['email'] = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Email inválido';
    }

    if (!formData.password.trim()) {
      newErrors['password'] = 'La contraseña es requerida';
    } else if (formData.password.length < 8) {
      newErrors['password'] = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (isSuperAdmin && !selectedOrganizationId) {
      newErrors['organizationId'] = 'La organización es requerida';
    }

    if (!formData.roleId) {
      newErrors['roleId'] = 'El rol es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: CreateEmployeeData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      roleId: formData.roleId,
    };

    // Super admins must specify the organization
    if (isSuperAdmin && selectedOrganizationId) {
      submitData.organizationId = selectedOrganizationId;
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      roleId: '',
    });
    setSelectedOrganizationId(organizationId || '');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <button
          className="fixed inset-0 bg-black opacity-60 cursor-default"
          onClick={handleClose}
          aria-label="Cerrar modal"
          type="button"
        />

        {/* Modal */}
        <div className="relative z-10 bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-brand-white">Agregar Usuario</h2>
            <button
              onClick={handleClose}
              className="text-muted hover:text-brand-white transition-colors"
              disabled={isLoading}
              type="button"
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Organization Selection (Super Admin Only) */}
            {isSuperAdmin && (
              <div>
                <label
                  htmlFor="organizationId"
                  className="block text-sm font-medium text-brand-white mb-1"
                >
                  Organización <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="organizationId"
                  value={selectedOrganizationId}
                  onChange={value => {
                    setSelectedOrganizationId(String(value));
                    // Reset role when organization changes
                    setFormData(prev => ({ ...prev, roleId: '' }));
                  }}
                  options={organizationOptions}
                  placeholder="Selecciona una organización"
                  searchable
                />
                {errors['organizationId'] && (
                  <p className="text-sm text-red-600 mt-1">{errors['organizationId']}</p>
                )}
              </div>
            )}

            <div>
              <Input
                id="name"
                label="Nombre Completo"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Juan Pérez García"
                required
              />
              {errors['name'] && <p className="text-sm text-red-600 mt-1">{errors['name']}</p>}
            </div>

            <div>
              <Input
                id="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Ej: juan.perez@empresa.com"
                required
              />
              {errors['email'] && <p className="text-sm text-red-600 mt-1">{errors['email']}</p>}
            </div>

            <div>
              <Input
                id="password"
                label="Contraseña"
                type="password"
                value={formData.password}
                onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Mínimo 8 caracteres"
                required
              />
              {errors['password'] && (
                <p className="text-sm text-red-600 mt-1">{errors['password']}</p>
              )}
            </div>

            <div>
              <label htmlFor="roleId" className="block text-sm font-medium text-brand-white mb-1">
                Rol <span className="text-red-500">*</span>
              </label>
              <Dropdown
                id="roleId"
                value={formData.roleId}
                onChange={value => setFormData(prev => ({ ...prev, roleId: String(value) }))}
                options={roleOptions}
                placeholder={
                  isSuperAdmin && !selectedOrganizationId
                    ? 'Primero selecciona una organización'
                    : 'Selecciona un rol'
                }
                disabled={isSuperAdmin && !selectedOrganizationId}
              />
              {errors['roleId'] && <p className="text-sm text-red-600 mt-1">{errors['roleId']}</p>}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button variant="white" onClick={handleClose} disabled={isLoading} type="button">
                Cancelar
              </Button>
              <Button type="submit" variant="black" disabled={isLoading}>
                {isLoading ? 'Creando...' : 'Crear Usuario'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
